import React, { useEffect, useState } from 'react';
import TailwindLayout from '~/libs/ui/atoms/TailwindLayout/TailwindLayout';
import { SpinningLoadingIcon } from '~/libs/ui/atoms/SpinningLoadingIcon';
import { getErpSessions } from '~/libs/infrastructure/api/users';
import { ErpSessionSimpleResponse } from 'com.cogvio.pricemonitor.api';
import { humanDateWithTimeFromString } from '~/libs/utils/format';
import { useAccessToken } from '~/libs/infrastructure/api/authentication';

export function IRPPage() {
	const accessToken = useAccessToken();

	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(true);
	const [sessions, setSessions] = useState<ErpSessionSimpleResponse[]>([]);
	const [selectedBrand, setSelectedBrand] = useState<string>('');
	const [listOfAvailableBrands, setListOfAvailableBrands] = useState<string[]>([]);

	const getSessionslist = (userId: string) => {
		getErpSessions(userId)
			.then((data) => {
				setSessions(data.items);

				setListOfAvailableBrands(
					data.items.map((item) => item.erpPack.brandName).filter((value, index, self) => self.indexOf(value) === index)
				);
			})
			.catch(() => {
				setError(true);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		if (accessToken === null) {
			return;
		}

		getSessionslist(accessToken.user?.id);
	}, [accessToken]);

	const renderedsessionList = sessions
		.filter((x) => (selectedBrand ? x.erpPack.brandName === selectedBrand : true))
		?.map((item) => {
			return (
				<div key={item.id} className='py-1'>
					<a
						href={`/irp/pack/${item.erpPack.id}/session/${item.id}`}
						className='flex flex-row flex-nowrap justify-between items-center w-full pl-1 pr-1 py-0.5 font-display tracking-wide rounded transition group hover:bg-gray-100'
					>
						<span className='grow-0 px-1.5 py-0.5 font-open text-label font-bold leading-5 text-gray-600 bg-gray-100 rounded whitespace-nowrap tabular-nums'>
							{humanDateWithTimeFromString(item.created)}
						</span>
						<h4 className='grow-0 px-2 text-md leading-5 font-semibold text-cPurple-500 whitespace-nowrap'>
							{item.erpPack.virtualProductPackName.split('-')[0]}
						</h4>
						<p className='grow shrink w-full pr-2 text-md leading-5 font-normal text-gray-600 tabular-nums truncate'>
							{item.erpPack.virtualProductPackName.split('-')[1]}
						</p>
						<span className='grow-0'>
							<span className='flex items-center justify-center w-4 h-4 rounded-full bg-gray-100'>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									className='h-3 w-3'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'
								>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M9 5l7 7-7 7'></path>
								</svg>
							</span>
						</span>
					</a>
				</div>
			);
		});

	return (
		<TailwindLayout>
			<div className='w-full h-full overflow-auto'>
				<div className='px-10 bg-cBlue-50 border-b border-gray-200'>
					<div className='py-6 w-full max-w-7xl mx-auto'>
						<h1 className='mb-2 font-display text-h2-head font-semibold text-gray-800'>
							International Reference Pricing
						</h1>
						<p className='mb-4 text-label text-gray-500'>
							IRP Module is an add-on for Price Monitor, an AI-powered system integrating over 100 global pricing and
							reimbursement data sources.
						</p>
					</div>
				</div>
				{loading && (
					<div className='w-full py-5 flex justify-center items-center'>
						<div className='h-12'>
							<SpinningLoadingIcon />
						</div>
					</div>
				)}
				{!loading && !error && listOfAvailableBrands && (
					<div className='px-10 pt-2 pb-10 bg-white'>
						{!sessions || sessions.length < 1 ? (
							<div className='w-full max-w-7xl py-3 mx-auto'>
								<div className='inline-block max-w-full mx-auto_ px-4 py-2.5 rounded-md bg-gray-100 bg-opacity-80 text-left'>
									<h2 className='leading-snug'>
										To see data here - please create session using{' '}
										<a
											href='/analytics/'
											className='text-cPurple-500 font-semibold underline underline-offset-2 decoration-2 transition hover:text-black'
										>
											Analytics
										</a>
										<br />
										or contact COGVIO Sales team for information.
									</h2>
								</div>
							</div>
						) : (
							<>
								<div className='w-full max-w-xs mx-auto'>
									<div className='pt-4 pb-6 w-full max-w-2xl mx-auto text-center'>
										<h3 className='mb-2 font-display font-semibold text-h3 text-gray-600'>Filter</h3>

										<div className='relative max-w-lg mb-2 mx-auto search'>
											<div className='flex items-stretch space-x-2'>
												<div className='w-full rounded border-2 border-gray-200 focus-within:border-cPurple-100 focus-within:ring-2 focus-within:ring-cPurple-50 transition-all'>
													<select
														name=''
														className={`w-full h-full px-2.5 py-3 font-display font-semibold ${
															selectedBrand ? 'text-cPurple-600' : 'text-gray-300'
														} border-0 rounded-l text-md outline-none focus:outline-none bg-white cursor-pointer`}
														defaultValue={'filter-brand-empty'}
														onChange={(e) => setSelectedBrand(e.target.value)}
													>
														<option value=''>Show all</option>
														<optgroup label='----------'></optgroup>

														{listOfAvailableBrands.map((opt) => (
															<option value={opt} key={opt}>
																{opt}
															</option>
														))}
													</select>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className='w-full max-w-2xl mx-auto'>
									<div className='mb-2 text-center'>
										<h3 className='font-display font-semibold text-h3 text-gray-600'>List</h3>
									</div>
									<div className='rounded bg-white border border-gray-200'>
										<div className='px-2 py-0.5 divide-y divide-gray-200'>{renderedsessionList}</div>
									</div>
								</div>
							</>
						)}
					</div>
				)}
			</div>
		</TailwindLayout>
	);
}
